/**
 * AudioPod SDK Types
 * Type definitions for the AudioPod API
 */

export interface AudioPodConfig {
  apiKey: string;
  baseURL?: string;
  timeout?: number;
  maxRetries?: number;
  debug?: boolean;
}

export interface ApiResponse<T = any> {
  data?: T;
  error?: string;
  message?: string;
  status?: string;
}

export interface Job {
  id: number;
  status: JobStatus;
  progress: number;
  createdAt: string;
  updatedAt?: string;
  completedAt?: string;
  errorMessage?: string;
  parameters?: Record<string, any>;
  result?: Record<string, any>;
}

export enum JobStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled'
}

export interface VoiceProfile {
  id: number;
  uuid: string;
  name: string;
  displayName?: string;
  description?: string;
  voiceType: VoiceType;
  provider: TTSProvider;
  isPublic: boolean;
  languageCode?: string;
  languageName?: string;
  gender?: string;
  accent?: string;
  createdAt?: string;
  status?: JobStatus;
}

export enum VoiceType {
  CUSTOM = 'custom',
  STANDARD = 'standard'
}

export enum TTSProvider {
  AUDIOPOD_SONIC = 'audiopod_sonic',
  OPENAI = 'openai',
  GOOGLE_GEMINI = 'google_gemini'
}

export interface VoiceCloneRequest {
  voiceFile: File | Blob | string;
  text: string;
  language?: string;
  speed?: number;
  waitForCompletion?: boolean;
  timeout?: number;
}

export interface VoiceCloneResult {
  job: Job;
  outputUrl?: string;
  duration?: number;
}

export interface MusicGenerationRequest {
  prompt: string;
  duration?: number;
  guidanceScale?: number;
  numInferenceSteps?: number;
  seed?: number;
  displayName?: string;
  waitForCompletion?: boolean;
  timeout?: number;
}

export interface MusicGenerationResult {
  job: Job;
  outputUrl?: string;
  outputUrls?: Record<string, string>;
  audioDuration?: number;
  actualSeeds?: number[];
  shareToken?: string;
  shareUrl?: string;
  isShared?: boolean;
}

export interface TranscriptionRequest {
  audioFile: File | Blob | string;
  language?: string;
  modelType?: 'whisperx' | 'faster-whisper';
  enableSpeakerDiarization?: boolean;
  enableWordTimestamps?: boolean;
  waitForCompletion?: boolean;
  timeout?: number;
}

export interface TranscriptionResult {
  job: Job;
  transcript?: string;
  detectedLanguage?: string;
  confidenceScore?: number;
  segments?: TranscriptionSegment[];
  audioDuration?: number;
}

export interface TranscriptionSegment {
  text: string;
  start: number;
  end: number;
  speaker?: string;
  confidence?: number;
}

export interface TranslationRequest {
  audioFile: File | Blob | string;
  targetLanguage: string;
  sourceLanguage?: string;
  waitForCompletion?: boolean;
  timeout?: number;
}

export interface TranslationResult {
  job: Job;
  sourceLanguage?: string;
  targetLanguage?: string;
  audioOutputUrl?: string;
  videoOutputUrl?: string;
  transcriptPath?: string;
  transcriptUrls?: {
    json?: string;
    source_audio?: string;
  };
}

export interface CreditInfo {
  balance: number;
  paygBalance: number;
  totalAvailableCredits: number;
  nextResetDate?: string;
  totalCreditsUsed: number;
}

export interface CreditUsageRecord {
  id: string;
  creditsUsed: number;
  serviceType: string;
  audioDuration: number;
  jobId?: string;
  creditSource: string;
  createdAt: string;
  usageMetadata?: Record<string, any>;
}

export interface AudioPodError {
  message: string;
  code?: string;
  statusCode?: number;
  details?: Record<string, any>;
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface JobProgress {
  jobId: number;
  status: JobStatus;
  progress: number;
  message?: string;
  result?: any;
}

// React Hook Types
export interface UseAudioPodOptions {
  apiKey?: string;
  onProgress?: (progress: JobProgress) => void;
  onError?: (error: AudioPodError) => void;
  onSuccess?: (result: any) => void;
}

export interface UseVoiceCloningOptions extends UseAudioPodOptions {
  autoStart?: boolean;
  pollInterval?: number;
}

export interface UseMusicGenerationOptions extends UseAudioPodOptions {
  autoStart?: boolean;
  pollInterval?: number;
}

export interface UseTranscriptionOptions extends UseAudioPodOptions {
  autoStart?: boolean;
  pollInterval?: number;
}

// Event Types for WebSocket
export interface AudioPodEvent {
  type: string;
  data: any;
  timestamp: string;
}

export interface JobStatusEvent extends AudioPodEvent {
  type: 'job_status';
  data: {
    jobId: number;
    status: JobStatus;
    progress: number;
    result?: any;
  };
}

export interface ProgressEvent extends AudioPodEvent {
  type: 'progress';
  data: {
    jobId: number;
    progress: number;
    message?: string;
  };
}

// Stem Extraction Types
export interface StemExtractionRequest {
  audioFile?: File | Blob | string;
  url?: string;
  stemTypes?: ('vocals' | 'drums' | 'bass' | 'other' | 'piano' | 'guitar')[];
  modelName?: 'htdemucs' | 'htdemucs_6s';
  twoStemsMode?: string;
  waitForCompletion?: boolean;
  timeout?: number;
}

export interface StemExtractionResult {
  job: Job;
  stemPaths?: Record<string, string>;
  downloadUrls?: Record<string, string>;
  qualityScores?: Record<string, number>;
}

// Additional Music Types
export interface RapGenerationRequest {
  prompt: string;
  lyrics: string;
  duration?: number;
  style?: 'modern' | 'classic' | 'trap';
  tempo?: number;
  displayName?: string;
  waitForCompletion?: boolean;
  timeout?: number;
}

export interface InstrumentalGenerationRequest {
  prompt: string;
  duration?: number;
  instruments?: string[];
  key?: string;
  tempo?: number;
  displayName?: string;
  waitForCompletion?: boolean;
  timeout?: number;
}

export interface VocalsGenerationRequest {
  prompt: string;
  lyrics: string;
  duration?: number;
  displayName?: string;
  waitForCompletion?: boolean;
  timeout?: number;
}

export interface SamplesGenerationRequest {
  prompt: string;
  sampleType?: string;
  tempo?: number;
  duration?: number;
  displayName?: string;
  waitForCompletion?: boolean;
  timeout?: number;
}

export interface Audio2AudioRequest {
  sourceFile: File | Blob | string;
  prompt: string;
  refAudioStrength?: number;
  audioDuration?: number;
  inferStep?: number;
  guidanceScale?: number;
  displayName?: string;
  waitForCompletion?: boolean;
  timeout?: number;
}

export interface SongBloomRequest {
  lyrics: string;
  referenceFile: File | Blob | string;
  duration?: number;
  guidanceScale?: number;
  numInferenceSteps?: number;
  seed?: number;
  displayName?: string;
  waitForCompletion?: boolean;
  timeout?: number;
}

export interface RetakeRequest {
  originalJobId: number;
  retakeVariance?: number;
  retakeSeeds?: number[];
  displayName?: string;
  waitForCompletion?: boolean;
  timeout?: number;
}

export interface RepaintRequest {
  sourceJobId: number;
  repaintStart: number;
  repaintEnd: number;
  prompt: string;
  lyrics?: string;
  displayName?: string;
  waitForCompletion?: boolean;
  timeout?: number;
}

export interface ExtendRequest {
  sourceJobId: number;
  leftExtendLength?: number;
  rightExtendLength?: number;
  prompt: string;
  lyrics?: string;
  extendSeeds?: number[];
  displayName?: string;
  waitForCompletion?: boolean;
  timeout?: number;
}

export interface EditRequest {
  sourceJobId: number;
  editTargetPrompt: string;
  editTargetLyrics?: string;
  editType?: 'remix' | 'only_lyrics';
  editNMin?: number;
  editNMax?: number;
  editNAvg?: number;
  displayName?: string;
  waitForCompletion?: boolean;
  timeout?: number;
}

// Voice Conversion Types
export interface VoiceConversionRequest {
  sourceFile?: File | Blob | string;
  url?: string;
  voiceUuid: string;
  waitForCompletion?: boolean;
  timeout?: number;
}

export interface VoiceConversionResult {
  job: Job;
  outputUrl?: string;
}

// Multi-Voice TTS Types
export interface MultiVoiceSegment {
  text: string;
  voiceId: number | string;
  startTime?: number;
  endTime?: number;
  speakerLabel?: string;
}

export interface MultiVoiceTTSRequest {
  segments: MultiVoiceSegment[];
  mixMode?: 'sequential' | 'parallel' | 'timed';
  outputFormat?: 'wav' | 'mp3' | 'ogg';
  silenceDuration?: number;
  normalizeVolume?: boolean;
  generationParams?: Record<string, any>;
  waitForCompletion?: boolean;
  timeout?: number;
}

export interface MultiVoiceTTSResult {
  job: Job;
  outputUrl?: string;
  totalDuration?: number;
  segmentsCount?: number;
  uniqueVoicesCount?: number;
}

// Voice Collection Types
export interface VoiceCollectionRequest {
  name: string;
  description?: string;
  isPublic?: boolean;
  color?: string;
  icon?: string;
  voiceIds?: number[];
}

export interface VoiceCollectionResult {
  collection: VoiceCollection;
  message: string;
}

export interface VoiceCollection {
  id: number;
  userId: string;
  name: string;
  description?: string;
  isPublic: boolean;
  color?: string;
  icon?: string;
  voiceCount?: number;
  voices?: VoiceProfile[];
  createdAt: string;
  updatedAt: string;
}

// Public Preview Types
export interface PublicPreviewRequest {
  voiceIdentifier: string;
  inputText: string;
  targetLanguage?: string;
}

export interface PublicPreviewResult {
  audioUrl: string;
  duration?: number;
}
